// AboutManager.js - Admin component for editing About section content
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Save, RefreshCw, Target, Eye, Heart, AlertCircle, CheckCircle, Plus, X, Trash2, FolderOpen, StepBack } from 'lucide-react';
import ENV from '../config/env';
import LogoutButton from './LogoutButton';

const API_BASE = ENV.API_BASE;

const AboutManager = () => {
    const [content, setContent] = useState({
        features: [],
        stats: [],
        mainTitle: '',
        mainDescription: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [serverStatus, setServerStatus] = useState('checking');
    const [editingFeature, setEditingFeature] = useState(null);
    const [editingStat, setEditingStat] = useState(null);
    const [error, setError] = useState(null);

    // Load content on mount
    useEffect(() => {
        checkServerStatus();
        loadContent();
    }, []);

    // Check server connectivity
    const checkServerStatus = async () => {
        try {
            const response = await fetch(`${API_BASE}/health`);
            setServerStatus(response.ok ? 'connected' : 'disconnected');
        } catch {
            setServerStatus('disconnected');
        }
    };

    // Status message hide unhide
    const showMessage = (msg) => {
        setMessage(msg);

        setTimeout(() => {
            setMessage({ type: '', text: '' });
        }, 4000);
    };

    // Load about content from server
    const loadContent = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE}/about`);
            if (response.ok) {
                const data = await response.json();
                setContent(data);
                showMessage({ type: 'success', text: 'Contenido cargado correctamente' });
            } else if (response.status === 404) {
                showMessage({ type: 'info', text: 'Usando contenido por defecto' });
            } else {
                throw new Error('Failed to load');
            }
        } catch (error) {
            showMessage({ type: 'error', text: 'Error al cargar el contenido' });
        } finally {
            setLoading(false);
        }
    };

    // Save content to server
    const saveContent = async () => {
        setSaving(true);
        setMessage({ type: '', text: '' });

        let resultMessage = { type: '', text: '' };

        try {
            const response = await fetch(`${API_BASE}/about`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(content)
            });

            if (response.ok) {
                resultMessage = { type: 'success', text: '✅ Contenido guardado correctamente' };
            } else {
                throw new Error('Save failed');
            }
        } catch (error) {
            resultMessage = { type: 'error', text: '❌ Error al guardar el contenido' };
        } finally {
            setTimeout(() => {
                setSaving(false);
                showMessage(resultMessage);
            }, 2000);
        }
    };

    // Update feature field
    const updateFeature = (index, field, value) => {
        setContent(prev => ({
            ...prev,
            features: prev.features.map((feature, i) =>
                i === index ? { ...feature, [field]: value } : feature
            )
        }));
    };

    // Update stat field
    const updateStat = (index, field, value) => {
        setContent(prev => ({
            ...prev,
            stats: prev.stats.map((stat, i) =>
                i === index ? { ...stat, [field]: value } : stat
            )
        }));
    };

    // Add new feature
    const addFeature = () => {
        setContent(prev => ({
            ...prev,
            features: [...prev.features, { icon: 'Target', title: 'Nueva característica', description: 'Descripción' }]
        }));
    };

    // Add new stat
    const addStat = () => {
        setContent(prev => ({
            ...prev,
            stats: [...prev.stats, { number: '0', label: 'Nueva estadística' }]
        }));
    };

    // Delete feature
    const deleteFeature = (index) => {
        if (!window.confirm('¿Estás seguro de que quieres eliminar esta característica?')) return;
        setContent(prev => ({
            ...prev,
            features: prev.features.filter((_, i) => i !== index)
        }));
    };

    // Delete stat
    const deleteStat = (index) => {
        if (!window.confirm('¿Estás seguro de que quieres eliminar esta estadística?')) return;
        setContent(prev => ({
            ...prev,
            stats: prev.stats.filter((_, i) => i !== index)
        }));
    };

    // Get icon component based on name
    const getIconComponent = (iconName) => {
        const icons = { Target, Eye, Heart };
        return icons[iconName] || Target;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 text-white p-4 md:p-6 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-400">Cargando contenido...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white p-4 md:p-6">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-8"
                >
                    <h1 className="text-3xl md:text-4xl font-bold gradient-text mb-4">
                        Gestor de Contenido - Nosotros
                    </h1>
                    <p className="text-gray-300">
                        Administra la sección Nosotros
                    </p>

                    {/* Server Status */}
                    <div className={`mt-4 inline-block px-4 py-2 rounded-full ${serverStatus === 'connected'
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-red-500/20 text-red-400'
                        }`}>
                        {serverStatus === 'connected'
                            ? '✅ Servidor de contenido conectado'
                            : '❌ Servidor de contenido desconectado'}
                    </div>

                    {error && (
                        <div className="mt-2 text-red-400 text-sm flex items-center justify-center gap-2">
                            <AlertCircle size={16} />
                            ⚠️ {error}
                        </div>
                    )}
                </motion.div>

                {/* Controls */}
                <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                    <div></div>
                    <div className="flex gap-4">
                        <button
                            onClick={() => window.history.back()}
                            className={`flex items-center gap-2 px-4 py-2 bg-secondary hover:bg-secondary/30 text-white rounded-lg smooth-transition`}
                            title="Regresar"
                        >
                            <StepBack size={18} />
                            <span>Regresar</span>
                        </button>
                        <LogoutButton />
                    </div>
                </div>

                {/* Main Title and Description */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gray-800 rounded-xl p-6 mb-8 border border-gray-700"
                >
                    <div className="flex items-center space-x-3 mb-6">
                        <div className="w-2 h-8 bg-primary rounded-full"></div>
                        <h2 className="text-xl font-bold text-white">
                            Título Principal y Descripción
                        </h2>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Título Principal</label>
                            <input
                                type="text"
                                value={content.mainTitle}
                                onChange={(e) => setContent(prev => ({ ...prev, mainTitle: e.target.value }))}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Descripción Principal</label>
                            <textarea
                                value={content.mainDescription}
                                onChange={(e) => setContent(prev => ({ ...prev, mainDescription: e.target.value }))}
                                rows="4"
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                            />
                        </div>
                    </div>
                </motion.div>

                {/* Features Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-gray-800 rounded-xl p-6 mb-8 border border-gray-700"
                >
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                            <div className="w-2 h-8 bg-primary rounded-full"></div>
                            <h2 className="text-xl font-bold text-white">
                                Características ({content.features.length})
                            </h2>
                        </div>
                        {/* <button
                            onClick={addFeature}
                            className="bg-primary hover:bg-primary/80 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all"
                        >
                            <Plus size={18} />
                            <span>Agregar Característica</span>
                        </button> */}
                    </div>

                    {content.features.length > 0 ? (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {content.features.map((feature, index) => {
                                const IconComponent = getIconComponent(feature.icon);
                                return (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.9 }}
                                        className="relative group bg-gray-700/50 rounded-lg p-4 border border-gray-600 hover:border-primary/50 transition-all"
                                    >
                                        {/* <button
                                            onClick={() => deleteFeature(index)}
                                            className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-all transform hover:scale-110"
                                        >
                                            <X size={14} />
                                        </button> */}

                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="text-primary">
                                                <IconComponent size={24} />
                                            </div>
                                            <select
                                                value={feature.icon}
                                                onChange={(e) => updateFeature(index, 'icon', e.target.value)}
                                                className="bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 flex-1"
                                            >
                                                <option value="Target">Target (Calidad)</option>
                                                <option value="Eye">Eye (Profesionalismo)</option>
                                                <option value="Heart">Heart (Pasión)</option>
                                            </select>
                                        </div>

                                        <input
                                            type="text"
                                            value={feature.title}
                                            onChange={(e) => updateFeature(index, 'title', e.target.value)}
                                            placeholder="Título"
                                            className="w-full bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white mb-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        />

                                        <textarea
                                            value={feature.description}
                                            onChange={(e) => updateFeature(index, 'description', e.target.value)}
                                            placeholder="Descripción"
                                            rows="2"
                                            className="w-full bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        />
                                    </motion.div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <FolderOpen size={48} className="mx-auto text-gray-600 mb-4" />
                            <h3 className="text-xl font-bold mb-2">No hay características</h3>
                            <p className="text-gray-400">
                                Agrega características usando el botón de arriba
                            </p>
                        </div>
                    )}
                </motion.div>

                {/* Stats Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-gray-800 rounded-xl p-6 border border-gray-700"
                >
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                            <div className="w-2 h-8 bg-primary rounded-full"></div>
                            <h2 className="text-xl font-bold text-white">
                                Estadísticas ({content.stats.length})
                            </h2>
                        </div>
                        <button
                            onClick={addStat}
                            className="bg-primary hover:bg-primary/80 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all"
                        >
                            <Plus size={18} />
                            <span>Agregar Estadística</span>
                        </button>
                    </div>

                    {content.stats.length > 0 ? (
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {content.stats.map((stat, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    className="relative group bg-gray-700/50 rounded-lg p-4 border border-gray-600 hover:border-primary/50 transition-all"
                                >
                                    <button
                                        onClick={() => deleteStat(index)}
                                        className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-all transform hover:scale-110"
                                    >
                                        <X size={14} />
                                    </button>

                                    <input
                                        type="text"
                                        value={stat.number}
                                        onChange={(e) => updateStat(index, 'number', e.target.value)}
                                        placeholder="Número"
                                        className="w-full bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white mb-2 text-center text-lg font-bold focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    />
                                    <input
                                        type="text"
                                        value={stat.label}
                                        onChange={(e) => updateStat(index, 'label', e.target.value)}
                                        placeholder="Etiqueta"
                                        className="w-full bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white text-center text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    />
                                </motion.div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <FolderOpen size={48} className="mx-auto text-gray-600 mb-4" />
                            <h3 className="text-xl font-bold mb-2">No hay estadísticas</h3>
                            <p className="text-gray-400">
                                Agrega estadísticas usando el botón de arriba
                            </p>
                        </div>
                    )}
                </motion.div>

                {/* Save Button */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="mt-8"
                >
                    <div className="flex flex-col justify-center items-center gap-4 mb-6 mt-6">
                        <div></div>
                        <div className="flex flex-col justify-center items-center gap-4">
                            <button
                                onClick={saveContent}
                                disabled={saving || serverStatus !== 'connected'}
                                className="bg-primary hover:bg-primary/80 flex items-center px-4 py-2 rounded-lg smooth-transition"
                            >
                                {saving ? (
                                    <RefreshCw size={20} className="animate-spin" />
                                ) : (
                                    <Save size={20} />
                                )}
                                <span>{saving ? 'Guardando...' : 'Guardar Cambios'}</span>
                            </button>
                            {message.text && (
                                <div className={`mt-4 p-4 rounded-lg flex items-center justify-center gap-3 ${message.type === 'success' ? 'bg-green-500/20 text-green-400' :
                                    message.type === 'error' ? 'bg-red-500/20 text-red-400' :
                                        'bg-blue-500/20 text-blue-400'
                                    }`}>
                                    {message.type === 'success' ? <CheckCircle size={20} /> :
                                        message.type === 'error' ? <AlertCircle size={20} /> :
                                            <AlertCircle size={20} />}
                                    <span>{message.text}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </motion.div>
            </div>
        </div >
    );
};

export default AboutManager;